// src/contexts/AuthContext.tsx
// GEOPTIMALISEERD: Firebase auth functies worden lazy geladen via dynamic import()
// Dit houdt firebase/auth (~90 KiB) uit het main bundle

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { firebaseReady, auth, googleProvider } from '../config/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<User>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Auth state listener met token refresh
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    firebaseReady.then(async () => {
      if (cancelled) return;

      const { onAuthStateChanged } = await import('firebase/auth');

      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (cancelled) return;
        if (user) {
          try {
            // Force token refresh om te checken of het nog geldig is
            await user.getIdToken(true);
            console.log('✅ Token refreshed successfully');
          } catch (error) {
            console.error('❌ Token refresh failed:', error);
          }
        }
        setUser(user);
        setLoading(false);
      });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  // Periodic token refresh (elke 50 minuten)
  useEffect(() => {
    if (!user) return;

    console.log('🔄 Starting periodic token refresh...');

    const intervalId = setInterval(async () => {
      try {
        await user.getIdToken(true);
        console.log('🔄 Token auto-refreshed at', new Date().toLocaleTimeString());
      } catch (error) {
        console.error('❌ Auto-refresh failed:', error);
      }
    }, 50 * 60 * 1000); // 50 minuten

    return () => {
      console.log('🛑 Stopping periodic token refresh');
      clearInterval(intervalId);
    };
  }, [user]);

  /**
   * Helper functie die wacht tot onAuthStateChanged vuurt met een user
   * Dit voorkomt de race condition waarbij navigate() gebeurt voordat user state is geüpdatet
   */
  const waitForAuthState = (): Promise<User> => {
    return new Promise(async (resolve, reject) => {
      const timeout = setTimeout(() => {
        unsubscribe();
        reject(new Error('Auth state timeout - geen response binnen 10 seconden'));
      }, 10000);

      const { onAuthStateChanged } = await import('firebase/auth');
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          clearTimeout(timeout);
          unsubscribe();
          resolve(user);
        }
      });
    });
  };

  const register = async (email: string, password: string): Promise<User> => {
    console.log('🔍 AuthContext register called!', { email });
    try {
      const { createUserWithEmailAndPassword } = await import('firebase/auth');
      await createUserWithEmailAndPassword(auth, email, password);
      console.log('✅ Registration successful, waiting for auth state...');

      // Wacht tot auth state is geüpdatet
      const user = await waitForAuthState();
      console.log('✅ Auth state updated, user ready:', user.email);

      // Stuur registratie notificatie (fire-and-forget)
      try {
        const token = await user.getIdToken();
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credits/register-notify`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {}); // Ignore errors - notificatie is niet kritiek
      } catch (e) {
        // Ignore - notificatie mag niet falen
      }

      return user;
    } catch (error) {
      console.error('❌ Registration error:', error);
      throw error;
    }
  };

  const login = async (email: string, password: string): Promise<User> => {
    console.log('🔍 AuthContext login called!', { email });
    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Login successful, waiting for auth state...');

      // Wacht tot auth state is geüpdatet
      const user = await waitForAuthState();
      console.log('✅ Auth state updated, user ready:', user.email);

      return user;
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const loginWithGoogle = async (): Promise<User> => {
    console.log('🔍 AuthContext Google login called!');
    try {
      const { signInWithPopup } = await import('firebase/auth');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ Google login successful, waiting for auth state...', result.user.email);

      // Check of dit een nieuwe user is
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;

      // Wacht tot auth state is geüpdatet
      const user = await waitForAuthState();
      console.log('✅ Auth state updated, user ready:', user.email);

      // Stuur registratie notificatie voor nieuwe users (fire-and-forget)
      if (isNewUser) {
        try {
          const token = await user.getIdToken();
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credits/register-notify`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }).catch(() => {});
        } catch (e) {
          // Ignore
        }
      }

      return user;
    } catch (error: any) {
      // Ignore de COOP warning als popup door user gesloten wordt
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('ℹ️ Popup closed by user');
        throw error;
      }
      console.error('❌ Google login error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
